import { errorSchema } from "@app/shared";
import {
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { ZodType } from "zod";
import { ApiError } from "../lib/api";
import {
  type AppliJeu,
  appliJeuSchema,
  type CreerLivreJeu,
  choixIllisiblesJeuSchema,
  conflitTexteJeuSchema,
  type EcartStructureJeu,
  type EcrireReglagesJeu,
  type EcrireStructureJeu,
  type ImporterYamlJeu,
  type ImportYamlJeu,
  importYamlJeuSchema,
  type ListeLivresJeu,
  type LivreJeu,
  type LivreJeuDetail,
  listeLivresJeuSchema,
  livreInvalideJeuSchema,
  livreJeuCreeSchema,
  livreJeuDetailSchema,
  type MiseEnPageJeu,
  miseEnPageJeuSchema,
  type OrigineJeu,
  type PublicationJeu,
  publicationFaiteJeuSchema,
  type RapportJeu,
  rapportJeuSchema,
  reglagesEcritsJeuSchema,
  type SceneJeu,
  type SceneJeuDetail,
  type SceneJeuResume,
  type ScenesJeu,
  sceneCreeeJeuSchema,
  sceneJeuDetailSchema,
  scenesJeuSchema,
  structureChangeeJeuSchema,
  structureEcriteJeuSchema,
  type TextesAvantJeu,
  texteEcritJeuSchema,
  textesAvantJeuSchema,
  yamlIllisibleJeuSchema,
  yamlJeuSchema,
} from "./contract";
import type { ResultatEcritureTexte, TexteConfirme } from "./sauvegarde-texte";

/**
 * Les lectures et les ecritures du module Livres-jeux (#276), sur le modele des Histoires, sans
 * rien leur importer.
 *
 * Les lectures ne reessayent pas : un 401, un 403 ou un 404 est une reponse, pas une panne.
 * Toutes les cles commencent par `livres-jeux`.
 *
 * Les refus qui portent un detail (les fautes d'un YAML ou de choix illisibles, le rapport d'un
 * livre que le moteur refuse, les ecarts d'une structure qui change) arrivent en `RefusJeu`,
 * une `ApiError` qui garde ce detail : l'ecran le montre.
 *
 * Le texte (la trame, le texte d'une scene) a son propre chemin (`ecrireTexteJeu`) : la
 * sauvegarde automatique (`sauvegarde-texte.ts`) lit le corps d'un 409, distingue le reseau
 * coupe du Lab a l'arret, et envoie la derniere saisie quand la page se ferme. Il ne leve pas.
 */

const commun = { retry: false, refetchOnWindowFocus: false } as const;

export const CLE_LIVRES_JEUX = ["livres-jeux"] as const;

export const cleLivres = (): readonly unknown[] => [...CLE_LIVRES_JEUX, "livres"];
export const cleLivre = (id: string): readonly unknown[] => [...CLE_LIVRES_JEUX, "livre", id];
export const cleScene = (id: string): readonly unknown[] => [...CLE_LIVRES_JEUX, "scene", id];
export const cleVerification = (id: string): readonly unknown[] => [
  ...CLE_LIVRES_JEUX,
  "verification",
  id,
];
export const cleAppli = (): readonly unknown[] => [...CLE_LIVRES_JEUX, "appli"];
/** Les textes d'avant de la trame (`scene` nul) ou d'une scene. */
export const cleTextesAvant = (livreId: number, scene: string | null): readonly unknown[] => [
  ...CLE_LIVRES_JEUX,
  "textes-avant",
  livreId,
  scene ?? "",
];

/** Un refus de la console, avec ce qu'il porte de plus qu'un message. */
export class RefusJeu extends ApiError {
  readonly erreurs: readonly string[];
  readonly rapport: RapportJeu | null;
  readonly ecarts: readonly EcartStructureJeu[];

  constructor(
    code: string,
    message: string,
    status: number,
    detail: {
      erreurs?: readonly string[] | undefined;
      rapport?: RapportJeu | null | undefined;
      ecarts?: readonly EcartStructureJeu[] | undefined;
    } = {},
  ) {
    super(code, message, status);
    this.name = "RefusJeu";
    this.erreurs = detail.erreurs ?? [];
    this.rapport = detail.rapport ?? null;
    this.ecarts = detail.ecarts ?? [];
  }
}

/**
 * Une requete JSON lue par un schema. Un refus devient un `RefusJeu` avec son code, son message
 * et son detail ; une requete qui ne part pas (reseau coupe), un `RefusJeu` de statut 0.
 */
export async function demander<T>(
  methode: "GET" | "POST" | "PUT",
  chemin: string,
  corps: unknown,
  schema: ZodType<T>,
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(chemin, {
      method: methode,
      headers: {
        accept: "application/json",
        ...(corps === undefined ? {} : { "content-type": "application/json" }),
      },
      ...(corps === undefined ? {} : { body: JSON.stringify(corps) }),
    });
  } catch {
    throw new RefusJeu("reseau", "La console ne répond pas : rien n'a changé.", 0);
  }
  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) throw refusDe(response.status, payload);
  const lu = schema.safeParse(payload);
  if (!lu.success) throw new RefusJeu("contract_broken", "Réponse hors contrat", response.status);
  return lu.data;
}

function refusDe(status: number, payload: unknown): RefusJeu {
  const fautes = choixIllisiblesJeuSchema.or(yamlIllisibleJeuSchema).safeParse(payload);
  if (fautes.success) {
    return new RefusJeu(fautes.data.error.code, fautes.data.error.message, status, {
      erreurs: fautes.data.erreurs,
    });
  }
  const invalide = livreInvalideJeuSchema.safeParse(payload);
  if (invalide.success) {
    return new RefusJeu(invalide.data.error.code, invalide.data.error.message, status, {
      rapport: invalide.data.rapport,
    });
  }
  const change = structureChangeeJeuSchema.safeParse(payload);
  if (change.success) {
    return new RefusJeu(change.data.error.code, change.data.error.message, status, {
      ecarts: change.data.ecarts,
    });
  }
  const erreur = errorSchema.safeParse(payload);
  return erreur.success
    ? new RefusJeu(erreur.data.error.code, erreur.data.error.message, status)
    : new RefusJeu("http_error", `Erreur HTTP ${status}`, status);
}

/** Le message a afficher sous un bouton quand une ecriture est refusee. */
export function messageDEchec(error: unknown): string {
  return error instanceof ApiError ? error.message : "L'écriture n'a pas abouti.";
}

// ---------------------------------------------------------------------------------------
// Les lectures

export function useLivresJeu(): UseQueryResult<ListeLivresJeu, Error> {
  return useQuery({
    queryKey: cleLivres(),
    queryFn: () => demander("GET", "/api/livres-jeux/livres", undefined, listeLivresJeuSchema),
    ...commun,
  });
}

export function useLivreJeu(id: string, actif = true): UseQueryResult<LivreJeuDetail, Error> {
  return useQuery({
    queryKey: cleLivre(id),
    queryFn: () =>
      demander(
        "GET",
        `/api/livres-jeux/livres/${encodeURIComponent(id)}`,
        undefined,
        livreJeuDetailSchema,
      ),
    enabled: actif,
    ...commun,
  });
}

export function useSceneJeu(id: string, actif = true): UseQueryResult<SceneJeuDetail, Error> {
  return useQuery({
    queryKey: cleScene(id),
    queryFn: () =>
      demander(
        "GET",
        `/api/livres-jeux/scenes/${encodeURIComponent(id)}`,
        undefined,
        sceneJeuDetailSchema,
      ),
    enabled: actif,
    ...commun,
  });
}

/** Ce que sert l'appli compagnon, relu chez elle : seulement quand l'onglet Publication s'ouvre. */
export function useAppliJeu(actif: boolean): UseQueryResult<AppliJeu, Error> {
  return useQuery({
    queryKey: cleAppli(),
    queryFn: () => demander("GET", "/api/livres-jeux/appli", undefined, appliJeuSchema),
    enabled: actif,
    ...commun,
  });
}

/**
 * La verification d'un livre, a la demande (`refetch`) : jamais d'elle-meme, le moteur calcule.
 * Le rapport reste en cache : l'onglet Scenes montre les fautes de chaque scene.
 */
export function useVerification(livreId: number): UseQueryResult<RapportJeu, Error> {
  return useQuery({
    queryKey: cleVerification(String(livreId)),
    queryFn: () =>
      demander(
        "GET",
        `/api/livres-jeux/livres/${livreId}/verification`,
        undefined,
        rapportJeuSchema,
      ),
    enabled: false,
    ...commun,
  });
}

// ---------------------------------------------------------------------------------------
// Les ecritures

/** Apres une ecriture qui change le livre : lui, et l'accueil. */
function relireLeLivre(client: QueryClient, livreId: number): void {
  void client.invalidateQueries({ queryKey: cleLivre(String(livreId)) });
  void client.invalidateQueries({ queryKey: cleLivres(), refetchType: "none" });
}

/**
 * Apres une ecriture qui change la structure du livre (scenes, ordre, choix, reglages, import) :
 * sa verification ne dit plus rien de juste, elle s'efface jusqu'a la prochaine. Le texte d'une
 * scene ne l'efface pas : les fautes restent la liste de ce qui est a reprendre.
 */
function oublierLaVerification(client: QueryClient, livreId: number): void {
  client.removeQueries({ queryKey: cleVerification(String(livreId)) });
}

export function useCreerLivreJeu(): UseMutationResult<
  { livre: { id: number } },
  Error,
  CreerLivreJeu
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (livre: CreerLivreJeu) =>
      demander("POST", "/api/livres-jeux/livres", livre, livreJeuCreeSchema),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: cleLivres() });
    },
  });
}

export function useEcrireReglages(
  livreId: number,
): UseMutationResult<{ livre: LivreJeu }, Error, EcrireReglagesJeu> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (reglages: EcrireReglagesJeu) =>
      demander(
        "PUT",
        `/api/livres-jeux/livres/${livreId}/reglages`,
        reglages,
        reglagesEcritsJeuSchema,
      ),
    onSuccess: () => {
      oublierLaVerification(client, livreId);
      // Les scenes montrent le pense-bete du livre (indices, jauges) : tout le module se relit.
      void client.invalidateQueries({ queryKey: CLE_LIVRES_JEUX });
    },
  });
}

export function useCreerScene(
  livreId: number,
): UseMutationResult<{ scene: SceneJeuResume }, Error, { cle: string; titre: string | null }> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (scene: { cle: string; titre: string | null }) =>
      demander("POST", `/api/livres-jeux/livres/${livreId}/scenes`, scene, sceneCreeeJeuSchema),
    onSuccess: () => {
      oublierLaVerification(client, livreId);
      relireLeLivre(client, livreId);
    },
  });
}

/**
 * Le nouvel ordre des scenes : il change les codes de sauvegarde d'un livre deja publie. L'ordre
 * rendu par la console entre dans le cache avant que les boutons ne se rouvrent : un second
 * deplacement part de lui, jamais de l'ordre d'avant (qui annulerait le premier).
 */
export function useOrdonnerScenes(
  livreId: number,
): UseMutationResult<ScenesJeu, Error, readonly number[]> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (ids: readonly number[]) =>
      demander("PUT", `/api/livres-jeux/livres/${livreId}/ordre`, { scenes: ids }, scenesJeuSchema),
    onSuccess: (reponse) => {
      client.setQueryData<LivreJeuDetail>(cleLivre(String(livreId)), (detail) =>
        detail ? { ...detail, scenes: reponse.scenes } : detail,
      );
      oublierLaVerification(client, livreId);
      relireLeLivre(client, livreId);
    },
  });
}

export function useSupprimerScene(): UseMutationResult<unknown, Error, number> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (sceneId: number) =>
      demander("POST", `/api/livres-jeux/scenes/${sceneId}/supprimer`, {}, scenesJeuSchema),
    onSuccess: () => {
      client.removeQueries({ queryKey: [...CLE_LIVRES_JEUX, "verification"] });
      void client.invalidateQueries({ queryKey: CLE_LIVRES_JEUX });
    },
  });
}

/** La structure d'une scene : 400 `choix_illisibles` porte les fautes du moteur. */
export function useEcrireStructure(
  sceneId: number,
  livreId: number,
): UseMutationResult<{ scene: SceneJeu }, Error, EcrireStructureJeu> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (structure: EcrireStructureJeu) =>
      demander(
        "PUT",
        `/api/livres-jeux/scenes/${sceneId}/structure`,
        structure,
        structureEcriteJeuSchema,
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: cleScene(String(sceneId)) });
      oublierLaVerification(client, livreId);
      relireLeLivre(client, livreId);
    },
  });
}

export function useMiseEnPage(livreId: number): UseMutationResult<MiseEnPageJeu, Error, void> {
  return useMutation({
    mutationFn: () =>
      demander("GET", `/api/livres-jeux/livres/${livreId}/pdf`, undefined, miseEnPageJeuSchema),
  });
}

export function useExportYaml(livreId: number): UseMutationResult<string, Error, void> {
  return useMutation({
    mutationFn: async () =>
      (await demander("GET", `/api/livres-jeux/livres/${livreId}/yaml`, undefined, yamlJeuSchema))
        .yaml,
  });
}

export function useImportYaml(
  livreId: number,
): UseMutationResult<ImportYamlJeu, Error, ImporterYamlJeu> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (corps: ImporterYamlJeu) =>
      demander("POST", `/api/livres-jeux/livres/${livreId}/yaml`, corps, importYamlJeuSchema),
    onSuccess: (reponse) => {
      if (reponse.livre === null) return;
      oublierLaVerification(client, livreId);
      void client.invalidateQueries({ queryKey: CLE_LIVRES_JEUX });
    },
  });
}

/**
 * Les textes d'avant de la trame (`scene` nul) ou d'une scene, quand la section s'ouvre : ce que
 * le journal garde d'un texte reecrit, importe par-dessus ou supprime.
 */
export function useTextesAvant(
  livreId: number,
  scene: string | null,
  actif: boolean,
): UseQueryResult<TextesAvantJeu, Error> {
  const parametre = scene === null ? "" : `?scene=${encodeURIComponent(scene)}`;
  return useQuery({
    queryKey: cleTextesAvant(livreId, scene),
    queryFn: () =>
      demander(
        "GET",
        `/api/livres-jeux/livres/${livreId}/textes-avant${parametre}`,
        undefined,
        textesAvantJeuSchema,
      ),
    enabled: actif,
    ...commun,
  });
}

export function usePublier(
  livreId: number,
): UseMutationResult<PublicationJeu, Error, { confirmer_structure: boolean }> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (corps: { confirmer_structure: boolean }) =>
      (
        await demander(
          "POST",
          `/api/livres-jeux/livres/${livreId}/publier`,
          corps,
          publicationFaiteJeuSchema,
        )
      ).publication,
    onSuccess: () => {
      relireLeLivre(client, livreId);
      void client.invalidateQueries({ queryKey: cleAppli() });
    },
  });
}

/** La page d'une scene en PNG : une adresse d'objet, a liberer quand l'image change. */
export async function apercuDeLaScene(sceneId: number): Promise<Blob> {
  let response: Response;
  try {
    response = await fetch(`/api/livres-jeux/scenes/${sceneId}/apercu`, {
      headers: { accept: "image/png" },
    });
  } catch {
    throw new RefusJeu("reseau", "La console ne répond pas.", 0);
  }
  if (!response.ok) {
    throw refusDe(response.status, await response.json().catch(() => null));
  }
  return response.blob();
}

// ---------------------------------------------------------------------------------------
// Le texte : la trame et le texte d'une scene

/**
 * Une ecriture qui ne repond pas en 30 s (le delai du frontal) est abandonnee et comptee hors
 * ligne. Si la console l'avait tout de meme enregistree, le nouvel essai fera un 409, que la
 * sauvegarde reconnait comme sa propre ecriture : rien ne s'ecrase, rien ne se perd.
 */
export const DELAI_ECRITURE_MS = 30_000;

export type ResultatTexteJeu = ResultatEcritureTexte<OrigineJeu>;

export async function ecrireTexteJeu(
  chemin: string,
  corps: { readonly texte: string; readonly version: number },
  keepalive: boolean,
): Promise<ResultatTexteJeu> {
  const abandon = new AbortController();
  const minuterie = setTimeout(() => abandon.abort(), DELAI_ECRITURE_MS);
  let response: Response;
  let payload: unknown;
  try {
    response = await fetch(chemin, {
      method: "PUT",
      headers: { accept: "application/json", "content-type": "application/json" },
      body: JSON.stringify(corps),
      signal: abandon.signal,
      // La derniere saisie part encore quand la page se ferme (au-dela de 64 Ko, le navigateur
      // refuse : la saisie reste alors dans le brouillon local).
      ...(keepalive ? { keepalive: true } : {}),
    });
    payload = await response.json().catch(() => null);
  } catch {
    return { type: "hors_ligne" };
  } finally {
    clearTimeout(minuterie);
  }
  if (response.ok) {
    const lu = texteEcritJeuSchema.safeParse(payload);
    return lu.success
      ? { type: "ok", reponse: lu.data }
      : {
          type: "refus",
          statut: response.status,
          message: "Réponse hors contrat : recharge la page pour voir ce qui est enregistré.",
        };
  }
  if (response.status === 409) {
    const conflit = conflitTexteJeuSchema.safeParse(payload);
    if (conflit.success) return { type: "conflit", courant: conflit.data.courant };
  }
  if (response.status === 503) return { type: "arret" };
  if (response.status === 401 || response.status === 403) {
    return { type: "session", statut: response.status };
  }
  const erreur = errorSchema.safeParse(payload);
  if (!erreur.success) {
    return { type: "refus", statut: response.status, message: `Erreur HTTP ${response.status}` };
  }
  // Une ecriture croisee sur le meme livre (un import au meme instant) : rien n'est ecrit, et la
  // sauvegarde reessaie seule, comme apres une panne (un statut 5xx l'y invite).
  const aReessayer = erreur.data.error.code === "ecriture_concurrente";
  return {
    type: "refus",
    statut: aReessayer ? 503 : response.status,
    code: erreur.data.error.code,
    message: erreur.data.error.message,
  };
}

/** Ou ecrit le texte : la trame d'un livre, ou le texte d'une scene. */
export type CibleDuTexte =
  | { readonly type: "trame"; readonly livreId: number }
  | { readonly type: "scene"; readonly sceneId: number; readonly livreId: number };

export function cheminDuTexte(cible: CibleDuTexte): string {
  return cible.type === "trame"
    ? `/api/livres-jeux/livres/${cible.livreId}/trame`
    : `/api/livres-jeux/scenes/${cible.sceneId}/texte`;
}

/**
 * Pose dans le cache ce que la console a maintenant, pour qu'un retour sur l'ecran reparte de la
 * bonne version : jamais par-dessus une version plus recente. Le reste du livre est marque
 * perime, sans relecture.
 */
export function poserLeTexte(
  client: QueryClient,
  cible: CibleDuTexte,
  texte: string,
  version: number,
  provenance: OrigineJeu | null,
): void {
  if (cible.type === "trame") {
    client.setQueryData<LivreJeuDetail>(cleLivre(String(cible.livreId)), (detail) =>
      detail === undefined || detail.livre.trame_version > version
        ? detail
        : {
            ...detail,
            livre: {
              ...detail.livre,
              trame: texte,
              trame_version: version,
              trame_provenance: provenance,
              trame_derniere_origine: "nicolas",
            },
          },
    );
  } else {
    client.setQueryData<SceneJeuDetail>(cleScene(String(cible.sceneId)), (detail) =>
      detail === undefined || detail.scene.texte_version > version
        ? detail
        : {
            ...detail,
            scene: {
              ...detail.scene,
              texte,
              texte_version: version,
              provenance,
              derniere_origine: "nicolas",
            },
          },
    );
    void client.invalidateQueries({
      queryKey: cleLivre(String(cible.livreId)),
      refetchType: "none",
    });
  }
  void client.invalidateQueries({ queryKey: cleLivres(), refetchType: "none" });
}

/** Ce que rend une ecriture confirmee, pose dans le cache. */
export function apresSauvegarde(
  client: QueryClient,
  cible: CibleDuTexte,
): (texte: string, reponse: TexteConfirme<OrigineJeu>) => void {
  return (texte, reponse) =>
    poserLeTexte(client, cible, texte, reponse.texte_version, reponse.provenance);
}

/** Relit ce qui porte le texte : le livre pour la trame, la scene pour son texte. */
export function relireLeTexte(client: QueryClient, cible: CibleDuTexte): void {
  void client.invalidateQueries({
    queryKey:
      cible.type === "trame" ? cleLivre(String(cible.livreId)) : cleScene(String(cible.sceneId)),
  });
  void client.invalidateQueries({ queryKey: cleLivres(), refetchType: "none" });
}
